// Persists demo bank-connection state in localStorage so the "connect bank"
// flow actually adds accounts that survive reloads — instead of the real PSD2
// redirect (which can't run in the demo). Two buckets:
//   - pending connections: accounts the user picked on the fake bank screen,
//     keyed by connectionId, awaiting the assignment/authorize step.
//   - added accounts: connections that completed authorization; merged into the
//     account list by the mock handlers.

export type DemoConnectedAccount = {
  id: string;
  name: string;
  iban: string;
  accountNumber: string;
  currency: "EUR" | "CZK" | "PLN" | "USD" | "GBP" | "HUF" | "RON" | "SEK" | "DKK" | "NOK";
  balance: number;
  // Bank presentation carried with the account so the account list can render
  // without a lookup into the static DEMO_BANKS table (connect-flow banks come
  // from the real paymentProvider enum, not our seed banks).
  bankName: string;
  bic: string;
  // Company the account is assigned to once authorized.
  companyId: string;
  // Checkbox state in the assignment dialog (undefined = untouched).
  selected?: boolean;
  // The user picked the company in the dialog (don't override with defaults).
  companyFromUser?: boolean;
};

export type DemoPendingConnection = {
  connectionId: string;
  paymentProvider: string;
  bankName: string;
  bic: string;
  accounts: DemoConnectedAccount[];
};

const PENDING_KEY = "fidoo-demo-pending-connections";
const ADDED_KEY = "fidoo-demo-added-accounts";

function readJson<T>(key: string, fallback: T): T {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function writeJson(key: string, value: unknown): void {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // storage disabled — demo will just not persist; non-fatal.
  }
}

export function savePendingConnection(connection: DemoPendingConnection): void {
  const all = readJson<Record<string, DemoPendingConnection>>(PENDING_KEY, {});
  all[connection.connectionId] = connection;
  writeJson(PENDING_KEY, all);
}

export function getPendingConnection(
  connectionId: string,
): DemoPendingConnection | undefined {
  return readJson<Record<string, DemoPendingConnection>>(PENDING_KEY, {})[
    connectionId
  ];
}

/**
 * Moves a pending connection's accounts into the persisted added-accounts list.
 * `isExisting` lets the caller reactivate accounts already in Fidoo (restore
 * from archive / reconnect) instead of adding them twice.
 */
export function commitConnection(
  connectionId: string,
  isExisting: (account: DemoConnectedAccount) => boolean = () => false,
): void {
  const pending = getPendingConnection(connectionId);
  if (!pending) return;

  const added = readJson<DemoConnectedAccount[]>(ADDED_KEY, []);
  const existingIds = new Set(added.map((a) => a.id));
  for (const account of pending.accounts) {
    if (isExisting(account)) continue;
    // Only accounts ticked in the assignment dialog get connected.
    if (account.selected !== true) continue;
    if (!existingIds.has(account.id)) {
      const { selected: _selected, companyFromUser: _fromUser, ...rest } = account;
      added.push(rest);
    }
  }
  writeJson(ADDED_KEY, added);

  const all = readJson<Record<string, DemoPendingConnection>>(PENDING_KEY, {});
  delete all[connectionId];
  writeJson(PENDING_KEY, all);
}

/** Applies an assignment-dialog change (checkbox / company) to a pending account. */
export function updatePendingAccount(
  entityId: string,
  patch: { selected?: boolean; companyId?: string; companyFromUser?: boolean },
): boolean {
  const all = readJson<Record<string, DemoPendingConnection>>(PENDING_KEY, {});
  for (const connection of Object.values(all)) {
    const account = connection.accounts.find((a) => a.id === entityId);
    if (account) {
      if (patch.selected !== undefined) account.selected = patch.selected;
      if (patch.companyId) account.companyId = patch.companyId;
      if (patch.companyFromUser) account.companyFromUser = true;
      writeJson(PENDING_KEY, all);
      return true;
    }
  }
  return false;
}

/** Commits every pending connection — the demo only has one in flight at a time. */
export function commitAllPendingConnections(
  isExisting?: (account: DemoConnectedAccount) => boolean,
): void {
  const all = readJson<Record<string, DemoPendingConnection>>(PENDING_KEY, {});
  Object.keys(all).forEach((id) => commitConnection(id, isExisting));
}

export function getAddedAccounts(): DemoConnectedAccount[] {
  return readJson<DemoConnectedAccount[]>(ADDED_KEY, []);
}
